import { takeLatest, put, call, select, take } from 'redux-saga/effects';
import { reportToSegmentAction } from "../actions";
import { reportToSegment, getProductTypeName, getKids, objectIsEmpty, slugify } from '../../utils';
import {
  SIGN_UP,
  SIGN_IN,
  CHANGE_BOOKING_STATUS,
  SEARCH,
  // GA
  VIEWED_PRODUCT_GA,
  CHECKED_OUT_ITEM_GA,
  BOOKED_ITEM_GA,
} from '../../utils/segmentEvents';

import {
  SEGMENT_REQUEST,
  CHECKOUT_HOTEL_SUCCESS,
  CHECKOUT_GIFT_SUCCESS,
  CHECKOUT_PASS_SUCCESS,
  SEGMENT_REQUEST_SUCCESS,
  SEGMENT_REQUEST_FAILURE,
  FETCH_USER_SUCCESS,
  BOOKING_MANAGEMENT_PROCESS_INIT,
  SIGNUP_SUCCESS,
  SELECT_CITY,
  VALIDATION_SUCCESS,
  UPDATE_PROFILE_SUCCESS,
  SOCIAL_SUCCESS,
  LOGIN_SUCCESS,
  OPEN_CHECKOUT_ITEM_PAGE,
  OPEN_CHECKOUT_GIFT_PAGE,
  OPEN_CHECKOUT_SUBSCRIPTION_PAGE,
  OPEN_PRODUCT_PAGE,
  OPEN_SEARCH_PAGE,
  UPDATE_USER_PRODUCT_PRICE_SUCCESS
} from "../types";

function* segmentRequest({ payload }) {
  try {
    const { type, name, properties, options } = payload;
    yield call(reportToSegment, type, name, properties, options);
    yield put({ type: SEGMENT_REQUEST_SUCCESS });
  } catch (error) {
    yield put({ type: SEGMENT_REQUEST_FAILURE });
  }
}

function* identifyUserSaga() {
  const userData = yield select(state => state.user.userData);

  if (userData) {
    const { emailAddress, firstName, lastName, userId, subscription, phone, zipCode, market, credits: { refferalCode } } = userData;

    yield put(reportToSegmentAction('identify', userId, {
      '$email': emailAddress,
      '$first_name': firstName && firstName.trim(),
      '$last_name': lastName && lastName.trim(),
      email: emailAddress,
      phone,
      zip_code: zipCode,
      referral_code: refferalCode,
      market_id: market && market.id,
      market_name: market && market.locationName,
      is_subscriber: !objectIsEmpty(subscription),
      subscriber_plan: !objectIsEmpty(subscription) ? subscription.name : null,
      customer_id: userId,
      referrer: document.referrer,
    }));
  }
}

function* onSignUpSaga() {
  const { emailAddress, firstName, lastName, userId, subscription, phone, zipCode, market, credits: { refferalCode } } = yield select(state => state.user.userData);

  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', SIGN_UP, {
    '$email': emailAddress,
    '$first_name': firstName && firstName.trim(),
    '$last_name': lastName && lastName.trim(),
    email: emailAddress,
    phone,
    zip_code: zipCode,
    referral_code: refferalCode,
    market_id: market && market.id,
    market_name: market && market.locationName,
    is_subscriber: !objectIsEmpty(subscription),
    subscriber_plan: !objectIsEmpty(subscription) ? subscription.name : null,
    customer_id: userId,
    referrer: document.referrer,
  }));
}

function* onSignInSaga() {
  const { emailAddress, firstName, lastName, userId, subscription, phone, zipCode, market, credits: { refferalCode } } = yield select(state => state.user.userData);

  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', SIGN_IN, {
    '$email': emailAddress,
    '$first_name': firstName && firstName.trim(),
    '$last_name': lastName && lastName.trim(),
    email: emailAddress,
    phone,
    zip_code: zipCode,
    referral_code: refferalCode,
    market_id: market && market.id,
    market_name: market && market.locationName,
    is_subscriber: !objectIsEmpty(subscription),
    subscriber_plan: !objectIsEmpty(subscription) ? subscription.name : null,
    customer_id: userId,
    referrer: document.referrer,
  }));
}

function* hotelSaga({ payload }) {
  const { id: bookingId, total, amount } = payload;
  const userProduct = yield select(state => state.userProduct);
  const guests = yield select(state => state.userPreferences.guests);

  const { product, hotel } = userProduct;
  const {
    id,
    isKidAllow,
    name: productName,
    image,
    type,
    price: {
      newPrice,
    }
  } = product;

  const {
    name,
    market,
    marketId,
    address,
    checkIn,
    hotelId,
  } = hotel;

  yield call(identifyUserSaga);
  yield take(BOOKING_MANAGEMENT_PROCESS_INIT);
  const { hotel: { checkinDate, redemption, expirationDate, payment, status } } = yield select(state => state.bookingManagement);
  yield put(reportToSegmentAction('track', BOOKED_ITEM_GA, {
    order_id: bookingId,
    content_type: 'product',
    content_category: productName,
    // content_name: `${name} - ${productName}`,
    content_ids: [id],
    affiliation: '',
    total,
    revenue: Number(total) - payment.tax,
    shipping: '',
    tax: payment.tax,
    service_fee: (Number(process.env.SERVIVE_FEE) || 0),
    currency: 'USD',
    products: [
      {
        product_id: id,
        sku: id,
        name: `${getProductTypeName(type)} - ${name} - ${productName}`,
        price: newPrice,
        category: getProductTypeName(type),
        url: `${window.location.origin}/l/${slugify(market)}/${slugify(name)}?pid=${id}hid=${hotelId}`,
        image_url: `${process.env.CDN_URL}${image}`,
      }
    ],
    // Klaviyo
    item_type: getProductTypeName(type),
    item_name: productName,
    item_hotel_name: name,
    item_id: id,
    item_validation_location: checkIn,
    item_location: `${address.neighborhood}, ${address.city}`,
    item_zip_code: address.zipCode,
    item_kids_allowed: getKids(isKidAllow),
    item_guest_count: guests,
    item_photo: `${process.env.CDN_URL}${image}`,
    item_hotel_id: hotelId,
    booking_expiration_date: expirationDate,
    booking_redemption_period: redemption,
    booking_id: bookingId,
    booking_url: `${window.location.origin}/purchase/${slugify(market)}/${slugify(name)}-${slugify(productName)}/${id}/${bookingId}`,
    booking_status: status,
    booking_checkin_date: checkinDate,
    market_id: marketId || 0,
    market_name: market,
    content_name: `${getProductTypeName(type)} - ${name} - ${productName}`,
    price: newPrice,
    quantity: amount,
  }));
}

function* giftSaga({ payload }) {
  const { id, total } = payload;

  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', BOOKED_ITEM_GA, {
    order_id: id,
    affiliation: '',
    total,
    revenue: total,
    shipping: '',
    tax: '',
    currency: 'USD',
    products: [
      {
        // product_id: id,
        // sku: id,
        name: 'eGiftCard',
        // category: 'eGiftCard',
        // price: total,
        url: window.location.href,
      }
    ],
  }));
}

function* passSaga({ payload }) {
  const { total } = payload;

  yield take(FETCH_USER_SUCCESS);
  const { subscription: { name, id, image }, zipCode } = yield select(state => state.user.userData);

  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', BOOKED_ITEM_GA, {
    order_id: id,
    affiliation: '',
    total,
    revenue: total,
    shipping: '',
    tax: '',
    currency: 'USD',
    products: [
      {
        product_id: id,
        sku: id,
        name,
        category: 'Subscription',
        price: total,
        url: window.location.href,
        image_url: `${process.env.CDN_URL}${image}`,
      }
    ],

    // Klaviyo
    // item_type: 'Subscription',
    item_name: name,
    item_id: id,
    item_zip_code: zipCode,
    item_photo: `${process.env.CDN_URL}${image}`,
    booking_status: 'Active',
    quantity: 1,
  }));
}

function* searchSaga() {
  if (window.location.pathname.includes('/s/')) {
    const city = yield select(state => state.userPreferences.city.label);
    const product = yield select(state => state.userPreferences.product);
    yield call(identifyUserSaga);
    yield put(reportToSegmentAction('track', SEARCH, {
      search_string: city,
      content_category: product.split(',').length > 1 ? 'Day Pass' : product,
      currency: 'USD',
    }));
  }
}

function* changeBookingStatusSaga() {
  const { emailAddress, userId } = yield select(state => state.user.userData);
  const { hotel: { bookingId, hotelPicture, productName, neighborhood, city, hotelName, marketName, marketId, productType, productId, status } } = yield select(state => state.bookingManagement);

  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', CHANGE_BOOKING_STATUS, {
    '$email': emailAddress,
    email: emailAddress,
    customer_id: userId,
    item_type: getProductTypeName(productType),
    item_name: productName,
    item_hotel_name: hotelName,
    item_id: productId,
    item_location: `${neighborhood}, ${city}`,
    item_photo: `${process.env.CDN_URL}${hotelPicture}`,
    booking_id: bookingId,
    booking_url: `${window.location.origin}/purchase/${slugify(marketName)}/${slugify(hotelName)}-${slugify(productName)}/${productId}/${bookingId}`,
    booking_status: status,
    market_id: marketId || 0,
    market_name: marketName,
  }));
}

function* openCheckoutItemSaga() {
  const { hotel, product } = yield select(state => state.userProduct);
  const { guests } = yield select(state => state.userPreferences);

  const {
    type,
    name,
    id,
    isKidAllow,
    image,
    description,
    price: {
      newPrice,
      oldPrice,
    },
  } = product;

  const {
    name: hotelName,
    address,
    market,
    marketId,
    hotelId,
  } = hotel;

  yield call(identifyUserSaga);

  yield put(reportToSegmentAction('track', CHECKED_OUT_ITEM_GA, {
    affiliation: '',
    value: newPrice,
    revenue: newPrice,
    shipping: '',
    currency: 'USD',
    content_type: 'product',
    content_ids: [id],
    content_name: `${name} at ${hotelName}`,
    products: [
      {
        product_id: id,
        sku: id,
        name: `${getProductTypeName(type)} - ${hotelName} - ${name}`,
        price: newPrice,
        category: getProductTypeName(type),
        url: `${window.location.origin}/l/${slugify(market)}/${slugify(hotelName)}?pid=${id}hid=${hotelId}`,
        image_url: `${process.env.CDN_URL}${image}`,
      }
    ],

    // Klaviyo
    item_type: getProductTypeName(type),
    item_name: name,
    item_hotel_name: hotelName,
    item_id: id,
    item_location: `${address.neighborhood}, ${address.city}`,
    item_kids_allowed: getKids(isKidAllow),
    item_guest_count: guests,
    item_photo: `${process.env.CDN_URL}${image}`,
    item_price: oldPrice,
    item_discount_price: newPrice,
    item_url: `${window.location.origin}/l/${slugify(market)}/${slugify(hotelName)}?pid=${id}hid=${hotelId}`,
    cart_url: window.location.href,
    item_description: description,
    market_id: marketId || 0,
    market_name: market,
  }));
}

function* openCheckoutGiftSaga() {
  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', CHECKED_OUT_ITEM_GA, {
    affiliation: '',
    shipping: '',
    tax: '',
    currency: 'USD',
    products: [
      {
        name: 'eGiftCard',
        category: 'eGiftCard',
        url: window.location.href,
      }
    ],
    item_name: 'eGiftCard',
    item_id: 'eGiftCard',
    item_zip_code: '',
  }));
}

function* openCheckoutSubscriptionSaga({ payload }) {
  const { name, type, id, guests, price, image } = payload;

  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', CHECKED_OUT_ITEM_GA, {
    affiliation: '',
    value: price,
    revenue: price,
    shipping: '',
    currency: 'USD',
    products: [
      {
        product_id: id,
        sku: id,
        name,
        price,
        category: 'Subscription',
        url: window.location.href,
        image_url: `${process.env.CDN_URL}${image}`,
      }
    ],

    // Klaviyo
    item_type: type,
    item_name: name,
    item_id: id,
    item_guest_count: guests,
    item_photo: `${process.env.CDN_URL}${image}`,
    item_price: price,
    cart_url: window.location.href,
  }));
}

function* openProductSaga() {
  yield take(UPDATE_USER_PRODUCT_PRICE_SUCCESS);
  const { hotel, product } = yield select(state => state.userProduct);
  const guests = yield select(state => state.userPreferences.guests);
  const {
    description,
    price: {
      newPrice,
      oldPrice
    },
    id,
    isKidAllow,
    name: productName,
    image,
    type,
  } = product;

  const {
    name,
    market,
    marketId,
    address,
    recommendation
  } = hotel;

  yield call(identifyUserSaga);
  yield put(reportToSegmentAction('track', VIEWED_PRODUCT_GA, {
    product_id: id,
    content_type: 'product',
    content_ids: [id],
    content_name: `${name} - ${productName}`,
    category: getProductTypeName(type),
    name: productName,
    price: newPrice,
    currency: 'USD',
    url: window.location.href,

    // Klaviyo
    image_url: `${process.env.CDN_URL}${image}`,
    item_what_you_get: description,
    item_description: recommendation,
    item_discount_price: newPrice,
    item_price: oldPrice,
    item_guest_count: guests,
    item_hotel_name: name,
    item_id: id,
    item_kids_allowed: getKids(isKidAllow),
    item_location: `${address.neighborhood}, ${address.city}`,
    item_name: productName,
    item_photo: `${process.env.CDN_URL}${image}`,
    item_type: getProductTypeName(type),
    item_url: window.location.href,
    market_id: marketId || 0,
    market_name: market,
  }));
}

export default function* saga() {
  yield takeLatest(CHECKOUT_HOTEL_SUCCESS, hotelSaga);
  yield takeLatest(CHECKOUT_GIFT_SUCCESS, giftSaga);
  yield takeLatest(CHECKOUT_PASS_SUCCESS, passSaga);
  yield takeLatest(SIGNUP_SUCCESS, onSignUpSaga);
  yield takeLatest([SELECT_CITY, OPEN_SEARCH_PAGE], searchSaga);
  yield takeLatest(VALIDATION_SUCCESS, changeBookingStatusSaga);
  yield takeLatest(UPDATE_PROFILE_SUCCESS, identifyUserSaga);
  yield takeLatest([SOCIAL_SUCCESS, LOGIN_SUCCESS], onSignInSaga);
  yield takeLatest(OPEN_CHECKOUT_ITEM_PAGE, openCheckoutItemSaga);
  yield takeLatest(OPEN_CHECKOUT_GIFT_PAGE, openCheckoutGiftSaga);
  yield takeLatest(OPEN_CHECKOUT_SUBSCRIPTION_PAGE, openCheckoutSubscriptionSaga);
  yield takeLatest(OPEN_PRODUCT_PAGE, openProductSaga);
  yield takeLatest(SEGMENT_REQUEST, segmentRequest);
};
